import React, { forwardRef } from 'react';
import {
    Box,
    Grid,
    Paper,
    Table,
    TableBody,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    TableCell,
    Table as MuiTable,
} from '@mui/material';
import { StyledTableCellPrint, StyledTableRow } from '../../reusableComponent/Reusables';
import qoiImg from '../../../images/print_header.png';
import trigaslogo from '../../../images/tri_gases.jpeg';
import styles from '../../../styles.module.css';

const ScheduleEntryPrint = forwardRef(({ printDetails }, ref) => {
    const printData = printDetails;

    let childRows = [];
    if (typeof printData.child_details === 'string') {
        try {
            childRows = JSON.parse(printData.child_details);
        } catch {
            childRows = [];
        }
    } else if (Array.isArray(printData.child_details)) {
        childRows = printData.child_details;
    }

    const dateConversion = (date) => {
        if (!date) return '-';
        const d = new Date(date);
        const dd = String(d.getDate()).padStart(2, '0');
        const mm = String(d.getMonth() + 1).padStart(2, '0');
        const yyyy = d.getFullYear();
        return `${dd}/${mm}/${yyyy}`;
    };

    // Order info split into two groups
    const leftInfo = [
        { label: 'Entry No', value: printData.entry_no },
        { label: 'Entry Date', value: dateConversion(printData.entry_date) },
    ];
    const rightInfo = [
        { label: 'PO No', value: printData.po_no },
        { label: 'PO Date', value: dateConversion(printData.po_date) },
        { label: 'Sales Officer', value: printData.sales_officer_name },
    ];

    return (
        <Box ref={ref} sx={{ margin: '30px' }}>
            <Paper
                sx={{
                    margin: '55px 15px 30px 15px',
                    boxShadow: 'none',
                    minHeight: '90vh',
                    backgroundColor: '#ffffff',
                    position: 'relative',
                    paddingBottom: '80px', // Reserve space for signature
                    padding: 2
                }}
            >
                {/* Header */}
                <Grid container>
                    <Grid
                        item
                        xs={12}
                        style={{
                            backgroundImage: `url(${qoiImg})`,
                            backgroundRepeat: 'no-repeat',
                            backgroundSize: 'cover',
                            backgroundPosition: 'center',
                            height: '60px',
                            marginBottom: '8px',
                        }}
                    >
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography
                                sx={{
                                    textAlign: 'start',
                                    marginLeft: '50px',
                                    marginTop: '10px',
                                    fontSize: '26px',
                                    fontWeight: 'bold',
                                    color: '#ffffff',
                                }}
                            >
                                CUSTOMER PO
                            </Typography>
                            <img
                                src={trigaslogo}
                                alt="Company Logo"
                                style={{
                                    height: '50px',
                                    marginTop: '5px',
                                    borderRadius: '2px',
                                    border: '2px solid #e10a0a',
                                }}
                            />
                        </Box>
                    </Grid>
                </Grid>

                {/* Customer Details */}
                <Grid container spacing={2}>
                    <Grid item xs={8}>
                        <Typography sx={{ fontWeight: 'bold', fontSize: '18px' }}>
                            {printData.customer_name}
                        </Typography>
                        <Typography sx={{ fontSize: '12px' }}>{printData.delivery_address}</Typography>
                    </Grid>
                </Grid>

                {/* Split Order Info */}
                <Grid container spacing={2} sx={{ marginTop: 2 }}>
                    <Grid item xs={6}>
                        <Grid container>
                            {leftInfo.map((item, index) => (
                                <React.Fragment key={index}>
                                    <Grid item xs={4}>
                                        <Typography className={styles.info_txt}>{item.label}</Typography>
                                    </Grid>
                                    <Grid item xs={1}>
                                        <Typography className={styles.info_txt}>:</Typography>
                                    </Grid>
                                    <Grid item xs={7}>
                                        <Typography className={styles.info_txt_ans} sx={{ textAlign: 'right' }}>
                                            {item.value}
                                        </Typography>
                                    </Grid>
                                </React.Fragment>
                            ))}
                        </Grid>
                    </Grid>
                    <Grid item xs={6}>
                        <Grid container>
                            {rightInfo.map((item, index) => (
                                <React.Fragment key={index}>
                                    <Grid item xs={4}>
                                        <Typography className={styles.info_txt}>{item.label}</Typography>
                                    </Grid>
                                    <Grid item xs={1}>
                                        <Typography className={styles.info_txt}>:</Typography>
                                    </Grid>
                                    <Grid item xs={7}>
                                        <Typography className={styles.info_txt_ans} sx={{ textAlign: 'right' }}>
                                            {item.value}
                                        </Typography>
                                    </Grid>
                                </React.Fragment>
                            ))}
                        </Grid>
                    </Grid>
                </Grid>

                {/* Product Table (fit to page) */}
                <TableContainer sx={{ marginTop: 3 }}>
                    <MuiTable
                        size="small"
                        style={{
                            borderCollapse: 'collapse',
                            tableLayout: 'fixed',
                            width: '100%',
                        }}
                    >
                        <TableHead>
                            <TableRow>
                                <StyledTableCellPrint align="center" sx={{ width: '5%' }}>Sr. No.</StyledTableCellPrint>
                                <StyledTableCellPrint align="left" sx={{ width: '10%' }}>Product Code</StyledTableCellPrint>
                                <StyledTableCellPrint align="left" sx={{ width: '20%' }}>Description</StyledTableCellPrint>
                                <StyledTableCellPrint align="right" sx={{ width: '8%' }}>Quantity</StyledTableCellPrint>
                                <StyledTableCellPrint align="left" sx={{ width: '6%' }}>UOM</StyledTableCellPrint>
                                <StyledTableCellPrint align="right" sx={{ width: '8%' }}>Unit Rate</StyledTableCellPrint>
                                <StyledTableCellPrint align="right" sx={{ width: '10%' }}>Transport Cost</StyledTableCellPrint>
                                <StyledTableCellPrint align="right" sx={{ width: '10%' }}>Basic Amount</StyledTableCellPrint>
                                <StyledTableCellPrint align="right" sx={{ width: '5%' }}>GST %</StyledTableCellPrint>
                                <StyledTableCellPrint align="right" sx={{ width: '10%' }}>GST Amount</StyledTableCellPrint>
                                <StyledTableCellPrint align="right" sx={{ width: '10%' }}>Total Amount</StyledTableCellPrint>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {childRows.map((row, idx) => (
                                <StyledTableRow key={idx}>
                                    <StyledTableCellPrint align="center">{idx + 1}</StyledTableCellPrint>
                                    <StyledTableCellPrint align="left">{row.prod_code}</StyledTableCellPrint>
                                    <StyledTableCellPrint align="left">{row.prod_description}</StyledTableCellPrint>
                                    <StyledTableCellPrint align="right">{row.qty_kg}</StyledTableCellPrint>
                                    <StyledTableCellPrint align="left">{row.uom}</StyledTableCellPrint>
                                    <StyledTableCellPrint align="right">{row.unit_rate}</StyledTableCellPrint>
                                    <StyledTableCellPrint align="right">{row.transport_cost}</StyledTableCellPrint>
                                    <StyledTableCellPrint align="right">{row.basic_amount}</StyledTableCellPrint>
                                    <StyledTableCellPrint align="right">{row.gst_percentage}</StyledTableCellPrint>
                                    <StyledTableCellPrint align="right">{row.gst_amount}</StyledTableCellPrint>
                                    <StyledTableCellPrint align="right">{row.total_amount}</StyledTableCellPrint>
                                </StyledTableRow>
                            ))}
                        </TableBody>
                    </MuiTable>
                </TableContainer>

                {/* Signature fixed to bottom-right of page */}
                <Box
                    sx={{
                        position: 'absolute',
                        bottom: 20,
                        right: 40,
                        textAlign: 'right',
                    }}
                >
                    <Typography sx={{ fontSize: '12px' }}>
                        For <b>{printData.customer_name}</b>
                    </Typography>
                    <Typography sx={{ fontSize: '12px' }}>Authorized Signatory</Typography>
                </Box>
            </Paper>
        </Box>
    );
});

export default ScheduleEntryPrint;
